import { alpha, Box, ButtonBase, CSSObject, Stack, styled, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { getServiceLevelColors } from 'src/graphql/entities/service-level/colors';
import { SwipeActionKind } from 'src/pages/CustomViews/custom-views-mobile-card-list/card-swipe.types';
import {
  getRecordRowBackgroundColor,
  RECORDS_LIST_BULK_BAR_CLEARANCE,
} from 'src/pages/CustomViews/records-list.styles';

export const CARD_SLOT_SIZE = '1.25rem';
const CARD_SLOT_ICON_SIZE = '1.125rem';
const CARD_TEXT_FONT_SIZE = '0.6875rem';

const TITLE_FONT_SIZE = '0.8125rem';
const TITLE_GLYPH_GAP = '0.3125rem';
const CHIP_GAP = '0.375rem';

const MONO_FONT_FAMILY = 'ui-monospace, SFMono-Regular, monospace';
const CARD_MIN_HEIGHT = '3.5rem';
const CHIP_INNER_GAP = '0.25rem';
export const CHIP_DOT_SIZE = '0.4375rem';
export const SLOT_DOT_SIZE = '0.625rem';
const SLOT_CHECK_ICON_SIZE = '0.75rem';

// the gesture measures the tray in px, so the action width is kept in px and converted once
export const CARD_SWIPE_ACTION_WIDTH_PX = 72;
const SWIPE_ACTION_FONT_SIZE = '0.65625rem';

const NAME_FONT_SIZE = '0.78125rem';
const TRAILING_TOP_FONT_SIZE = '0.71875rem';
const TRAILING_COLUMN_MAX_WIDTH = '8.125rem';

// Cell renderers bring their own Typography (body2 or 1rem); inside the card the surrounding
// element owns the font so a title, a time and a pill each read at their own size.
const inheritCellTypography = {
  '& .MuiTypography-root': { font: 'inherit' },
};

export const CardListScroll = styled(Box, { name: 'CardListScroll' })(({ theme }) => ({
  height: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingBottom: RECORDS_LIST_BULK_BAR_CLEARANCE,
  backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
}));

export const VirtualRows = styled('div', { name: 'VirtualRows' })({
  position: 'relative',
});

export const VirtualRow = styled('div', { name: 'VirtualRow' })({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
});

export const CardRoot = styled('div', {
  name: 'MobileRecordCard',
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'hasMinHeight',
})<{ isActive: boolean; hasMinHeight: boolean }>(({ theme, isActive, hasMinHeight }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  // a one-line card is centered in a row that is as tall as a two-line one is short
  ...(hasMinHeight && { minHeight: CARD_MIN_HEIGHT }),
  borderBottom: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  backgroundColor: getRecordRowBackgroundColor(theme, isActive),
  ...(!isActive && {
    '&:active': { backgroundColor: alpha(theme.palette.custom.OV.sys.onSurface, 0.04) },
    '@media (hover: hover)': {
      '&:hover': { backgroundColor: alpha(theme.palette.custom.OV.sys.onSurface, 0.04) },
    },
  }),
}));

export const CardSlot = styled(Box, { name: 'CardSlot' })(({ theme }) => ({
  flexShrink: 0,
  width: CARD_SLOT_SIZE,
  height: CARD_SLOT_SIZE,
  display: 'grid',
  placeItems: 'center',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  '& svg, & .material-symbols-outlined': {
    width: CARD_SLOT_ICON_SIZE,
    height: CARD_SLOT_ICON_SIZE,
    fontSize: CARD_SLOT_ICON_SIZE,
  },
}));

export const SlotCheck = styled('span', { name: 'SlotCheck' })(({ theme }) => ({
  display: 'grid',
  placeItems: 'center',
  width: CARD_SLOT_ICON_SIZE,
  height: CARD_SLOT_ICON_SIZE,
  borderRadius: '50%',
  backgroundColor: alpha(theme.palette.custom.OV.sys.success, 0.22),
  color: theme.palette.custom.OV.sys.success,
  // doubled so the slot's own `& svg` icon size does not win
  '&& svg': { width: SLOT_CHECK_ICON_SIZE, height: SLOT_CHECK_ICON_SIZE },
}));

export const CardBody = styled(Stack, { name: 'CardBody' })(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  gap: theme.spacing(0.25),
}));

export const CardTitleRow = styled(Stack, { name: 'CardTitleRow' })(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  minWidth: 0,
}));

export const CardTitle = styled(Stack, { name: 'CardTitle' })(({ theme }) => ({
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
  gap: TITLE_GLYPH_GAP,
  minWidth: 0,
  fontSize: TITLE_FONT_SIZE,
  fontWeight: 500,
  lineHeight: 1.4,
  letterSpacing: '-0.005em',
  color: theme.palette.custom.OV.sys.onSurface,
  ...inheritCellTypography,
}));

export const CardTitleText = styled('span', { name: 'CardTitleText' })({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const CardTime = styled(Box, { name: 'CardTime' })(({ theme }) => ({
  flexShrink: 0,
  marginLeft: 'auto',
  fontSize: CARD_TEXT_FONT_SIZE,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  ...inheritCellTypography,
}));

export const CardTextColumn = styled(Stack, { name: 'CardTextColumn' })({
  flex: 1,
  minWidth: 0,
  gap: '0.0625rem',
});

const ellipsisLine: CSSObject = {
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const CardName = styled('span', { name: 'CardName' })(({ theme }) => ({
  ...ellipsisLine,
  fontSize: NAME_FONT_SIZE,
  fontWeight: 600,
  lineHeight: 1.25,
  color: theme.palette.custom.OV.sys.onSurface,
  ...inheritCellTypography,
}));

export const CardSubtitle = styled('span', { name: 'CardSubtitle' })(({ theme }) => ({
  ...ellipsisLine,
  fontSize: CARD_TEXT_FONT_SIZE,
  lineHeight: 1.25,
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  ...inheritCellTypography,
}));

export const CardTrailingColumn = styled(Stack, { name: 'CardTrailingColumn' })({
  flexShrink: 0,
  marginLeft: 'auto',
  alignItems: 'flex-end',
  gap: '0.125rem',
  maxWidth: TRAILING_COLUMN_MAX_WIDTH,
  textAlign: 'right',
});

export const CardTrailingTop = styled('span', { name: 'CardTrailingTop' })(({ theme }) => ({
  ...ellipsisLine,
  maxWidth: '100%',
  fontSize: TRAILING_TOP_FONT_SIZE,
  lineHeight: 1.4,
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  '& .MuiChip-root': { fontSize: CARD_TEXT_FONT_SIZE },
  ...inheritCellTypography,
}));

export const CardTrailingBottom = styled(CardTime, { name: 'CardTrailingBottom' })(
  ({ theme }) => ({
    color: alpha(theme.palette.custom.OV.sys.onSurfaceVariant, 0.85),
  }),
);

export const ChipLineRoot = styled(Stack, {
  name: 'ChipLineRoot',
  shouldForwardProp: (prop) => prop !== 'isFirstLine',
})<{ isFirstLine: boolean }>(({ isFirstLine }) => ({
  position: 'relative',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'nowrap',
  gap: CHIP_GAP,
  minWidth: 0,
  overflow: 'hidden',
  // with the title off the chips are the first line and share it with the time
  ...(isFirstLine ? { flex: 1 } : { marginTop: '0.25rem' }),
}));

export const ChipSlot = styled('div', {
  name: 'ChipSlot',
  shouldForwardProp: (prop) => prop !== 'isHidden',
})<{ isHidden: boolean }>(({ isHidden }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  whiteSpace: 'nowrap',
  // a Chip drawn by a renderer keeps its look but not its own size, which would out-size the pills
  '& .MuiChip-root': { fontSize: CARD_TEXT_FONT_SIZE },
  // a skipped chip keeps its box so the next fit pass can still measure it
  ...(isHidden && { position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }),
}));

export const ChipPillContent = styled('span', { name: 'ChipPillContent' })({
  display: 'inline-flex',
  alignItems: 'center',
  verticalAlign: 'top',
  gap: CHIP_INNER_GAP,
  ...inheritCellTypography,
});

export const ChipMonoContent = styled(ChipPillContent, { name: 'ChipMonoContent' })({
  fontFamily: MONO_FONT_FAMILY,
  letterSpacing: 'normal',
});

export const ChipDash = styled('span', { name: 'ChipDash' })({ opacity: 0.7 });

export const ServiceLevelDot = styled('span', {
  name: 'ServiceLevelDot',
  shouldForwardProp: (prop) => prop !== 'serviceLevel' && prop !== 'dotSize',
})<{ serviceLevel: string; dotSize: string }>(({ theme, serviceLevel, dotSize }) => ({
  flexShrink: 0,
  width: dotSize,
  height: dotSize,
  borderRadius: '50%',
  backgroundColor:
    getServiceLevelColors(serviceLevel, theme).color || theme.palette.custom.OV.sys.outline,
}));

export const ChipIdText = styled('span', { name: 'ChipIdText' })(({ theme }) => ({
  fontFamily: MONO_FONT_FAMILY,
  fontSize: CARD_TEXT_FONT_SIZE,
  fontWeight: 500,
  lineHeight: 1.2,
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
}));

export const SwipeRow = styled('div', { name: 'CardSwipeRow' })({
  position: 'relative',
  overflow: 'hidden',
  touchAction: 'pan-y',
});

export const SwipeTray = styled(motion.div, { name: 'CardSwipeTray' })({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  overflow: 'hidden',
});

export const SwipeActionButton = styled(ButtonBase, {
  name: 'CardSwipeAction',
  shouldForwardProp: (prop) => prop !== 'kind',
})<{ kind: SwipeActionKind }>(({ theme, kind }) => {
  const { sys, extended } = theme.palette.custom.OV;
  const toneByKind: Record<SwipeActionKind, { background: string; color: string }> = {
    [SwipeActionKind.Snooze]: {
      background: extended.Proactive.color,
      color: extended.Proactive.onColor,
    },
    [SwipeActionKind.Solve]: { background: sys.primary, color: sys.onPrimary },
  };

  return {
    flex: `0 0 ${theme.typography.pxToRem(CARD_SWIPE_ACTION_WIDTH_PX)}`,
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0, 0.5),
    fontSize: SWIPE_ACTION_FONT_SIZE,
    fontWeight: 600,
    lineHeight: 1.2,
    textAlign: 'center',
    backgroundColor: toneByKind[kind].background,
    color: toneByKind[kind].color,
  };
});

export const SkeletonCard = styled('div', { name: 'SkeletonCard' })(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
}));

export const EmptyState = styled(Typography, { name: 'CardListEmptyState' })(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
}));
